import { useMemo, useState } from "react";
import { Button } from "@fprint/ui";
import type { Material, QuoteParams } from "@fprint/shared";

interface QuoteFormProps {
  materials: Material[];
  disabled?: boolean;
  onSubmit: (params: QuoteParams) => void;
}

export function QuoteForm({ materials, disabled, onSubmit }: QuoteFormProps) {
  const [materialId, setMaterialId] = useState(materials[0]?.id ?? "");
  const [colorId, setColorId] = useState(materials[0]?.colors?.[0]?.id ?? "");
  const [designWork, setDesignWork] = useState("");
  const [finishWork, setFinishWork] = useState("");
  const [notes, setNotes] = useState("");

  const colors = useMemo(
    () => materials.find((m) => m.id === materialId)?.colors ?? [],
    [materials, materialId],
  );

  // Live routing preview (owner-required UX): design/finish work always
  // routes the job to a MANUAL operator quote; notes alone trigger a quick
  // operator review before payment. Tell the customer as they type.
  const needsManualQuote =
    designWork.trim() !== "" || finishWork.trim() !== "";
  const needsReview = !needsManualQuote && notes.trim() !== "";

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!materialId || !colorId) return;
    onSubmit({
      material_id: materialId,
      color_id: colorId,
      scale_factor: 1.0, // default; actual scale comes from the preview control
      notes: notes.trim() || null,
      design_work: designWork.trim() || null,
      finish_work: finishWork.trim() || null,
    });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col gap-4"
      data-testid="quote-form"
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Material</span>
        <select
          data-testid="material-select"
          value={materialId}
          onChange={(e) => {
            const next = e.target.value;
            setMaterialId(next);
            const first = materials.find((m) => m.id === next)?.colors?.[0]?.id ?? "";
            setColorId(first);
          }}
          className="h-10 rounded-md border border-border bg-bg px-2"
          disabled={disabled}
        >
          {materials.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Colour</span>
        <select
          data-testid="color-select"
          value={colorId}
          onChange={(e) => setColorId(e.target.value)}
          className="h-10 rounded-md border border-border bg-bg px-2"
          disabled={disabled || colors.length === 0}
        >
          {colors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Design work (optional)</span>
        <input
          type="text"
          data-testid="design-work"
          value={designWork}
          onChange={(e) => setDesignWork(e.target.value)}
          placeholder="e.g. adapt or create the model, fix walls, split for printing"
          className="h-10 rounded-md border border-border bg-bg px-2"
          disabled={disabled}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Finish work (optional)</span>
        <input
          type="text"
          data-testid="finish-work"
          value={finishWork}
          onChange={(e) => setFinishWork(e.target.value)}
          placeholder="e.g. priming, sanding, painting, clear coat"
          className="h-10 rounded-md border border-border bg-bg px-2"
          disabled={disabled}
        />
      </label>

      {needsManualQuote && (
        <p
          role="status"
          data-testid="manual-quote-notice"
          className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-fg"
        >
          Design or finish work is quoted personally by our operator — this
          order will get a manual quote by email instead of instant checkout.
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Preferred settings / notes (optional)</span>
        <textarea
          data-testid="preferred-settings"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. 0.15mm layer, 30% infill, add supports, etc."
          className="min-h-[80px] rounded-md border border-border bg-bg px-2 py-1 text-sm"
          disabled={disabled}
        />
      </label>

      {needsReview && (
        <p
          role="status"
          data-testid="review-notice"
          className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-xs text-warning-fg"
        >
          Notes are checked by our operator — your quote will need a quick
          review before payment.
        </p>
      )}

      <Button type="submit" size="lg" disabled={disabled} data-testid="quote-submit">
        Get quote
      </Button>
    </form>
  );
}
